import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Checkbox from '@mui/joy/Checkbox';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import CircularProgress from '@mui/joy/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { clientsApi } from '../../api/clients.api';
import { addressesApi } from '../../api/addresses.api';
import { Client } from '../../types/client';
import { Address, CreateAddressDto } from '../../types/address';
import { LAYOUT } from '../../theme/layoutTokens';
import { formatRecordReference } from '../../utils/formatRecordReference';
import ArgentinaProvinceCityFields from '../../components/addresses/ArgentinaProvinceCityFields';

interface ClientFormData {
  name: string;
  phoneNumber: string;
}

const ClientForm: React.FC = () => {
  const navigate = useNavigate();
  const { reference } = useParams<{ reference?: string }>();
  const clientReference = reference as string | undefined;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ClientFormData>({
    name: '',
    phoneNumber: '',
  });
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressFormData, setAddressFormData] = useState<CreateAddressDto>({
    street: '',
    number: '',
    city: '',
    state: '',
    zipCode: '',
    referenceDescription: '',
    isDefault: false,
  });

  useEffect(() => {
    if (clientReference) {
      loadClient(clientReference);
    }
  }, [clientReference]);

  const loadClient = async (reference: string) => {
    try {
      setLoading(true);
      const [client, clientAddresses] = await Promise.all([
        clientsApi.getOne(reference),
        addressesApi.getByClientReference(reference),
      ]);
      setFormData({
        name: client.name,
        phoneNumber: client.phoneNumber ?? '',
      });
      setAddresses(clientAddresses);
    } catch (error) {
      console.error('Error loading client:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      let savedClient: Client;
      if (clientReference) {
        savedClient = await clientsApi.update(clientReference, formData);
      } else {
        savedClient = await clientsApi.create(formData);
        if (addresses.length > 0) {
          await Promise.all(
            addresses.map((addr) =>
              addressesApi.create(savedClient.reference, {
                street: addr.street,
                number: addr.number,
                city: addr.city,
                state: addr.state,
                zipCode: addr.zipCode,
                isDefault: addr.isDefault,
                ...(addr.reference_note
                  ? { referenceDescription: addr.reference_note }
                  : {}),
              }),
            ),
          );
        }
      }
      navigate(`/clients/${savedClient.reference}`);
    } catch (error) {
      console.error('Error saving client:', error);
    } finally {
      setLoading(false);
    }
  };

  const commitInlineAddress = async (): Promise<void> => {
    const payload: CreateAddressDto = {
      street: addressFormData.street.trim(),
      number: addressFormData.number.trim(),
      city: addressFormData.city.trim(),
      state: addressFormData.state.trim(),
      zipCode: addressFormData.zipCode.trim(),
      isDefault: addressFormData.isDefault,
      ...(addressFormData.referenceDescription?.trim()
        ? { referenceDescription: addressFormData.referenceDescription.trim() }
        : {}),
    };
    if (
      !payload.street ||
      !payload.number ||
      !payload.city ||
      !payload.state ||
      !payload.zipCode
    ) {
      return;
    }
    try {
      if (clientReference) {
        const created = await addressesApi.create(clientReference, payload);
        setAddresses((prev) => [...prev, created]);
      } else {
        setAddresses((prev) => [
          ...prev,
          {
            reference: `temp-${Date.now()}`,
            clientReference: '',
            street: payload.street,
            number: payload.number,
            city: payload.city,
            state: payload.state,
            zipCode: payload.zipCode,
            isDefault: payload.isDefault,
            ...(payload.referenceDescription
              ? { reference_note: payload.referenceDescription }
              : {}),
          },
        ]);
      }
      setAddressFormData({
        street: '',
        number: '',
        city: '',
        state: '',
        zipCode: '',
        referenceDescription: '',
        isDefault: false,
      });
      setShowAddressForm(false);
    } catch (error) {
      console.error('Error saving address:', error);
    }
  };

  const handleRemoveAddress = (addressToRemove: Address) => {
    setAddresses(addresses.filter((addr) => addr !== addressToRemove));
  };

  if (loading && clientReference) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={LAYOUT.stackSection} sx={{ width: '100%', maxWidth: LAYOUT.formMaxWidth, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={LAYOUT.stackDense}>
        <Box>
          <Typography level="h2">
            {clientReference
              ? `Editar cliente #${formatRecordReference(clientReference)}`
              : 'Nuevo cliente'}
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.600', mt: 0.5 }}>
            {clientReference ? 'Actualizá la información del cliente' : 'Ingresá los datos del nuevo cliente'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<ArrowBackIcon />}
          onClick={() => navigate('/clients')}
        >
          Volver
        </Button>
      </Stack>

      <Sheet variant="outlined" sx={{ borderRadius: 'md', boxShadow: 'sm', overflow: 'hidden' }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ p: LAYOUT.cardPaddingLg, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography level="title-lg" sx={{ mb: LAYOUT.stackTight }}>
              Información principal
            </Typography>
            <Grid container spacing={LAYOUT.gridSpacing}>
              <Grid xs={12} md={6}>
                <FormControl required>
                  <FormLabel>Nombre completo</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Juan Pérez"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} md={6}>
                <FormControl required>
                  <FormLabel>Teléfono</FormLabel>
                  <Input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    placeholder="549XXXXXXXXXX"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ p: LAYOUT.cardPaddingLg }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: LAYOUT.stackTight }} flexWrap="wrap" gap={LAYOUT.stackDense}>
              <Box>
                <Typography level="title-lg">Direcciones</Typography>
                <Typography level="body-sm" sx={{ color: 'neutral.600', mt: 0.5 }}>
                  Agregá una o más direcciones para el cliente
                </Typography>
              </Box>
              <Button
                type="button"
                startDecorator={<AddIcon />}
                color="primary"
                onClick={() => setShowAddressForm(true)}
              >
                Agregar dirección
              </Button>
            </Stack>

            {showAddressForm ? (
              <Sheet variant="soft" sx={{ p: LAYOUT.cardPaddingDense, mb: LAYOUT.stackTight, borderRadius: 'md' }}>
                <Box>
                  <Grid container spacing={LAYOUT.gridSpacing}>
                    <Grid xs={12} md={6}>
                      <FormControl required>
                        <FormLabel>Calle</FormLabel>
                        <Input
                          value={addressFormData.street}
                          onChange={(e) =>
                            setAddressFormData({ ...addressFormData, street: e.target.value })
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl required>
                        <FormLabel>Número</FormLabel>
                        <Input
                          value={addressFormData.number}
                          onChange={(e) =>
                            setAddressFormData({ ...addressFormData, number: e.target.value })
                          }
                        />
                      </FormControl>
                    </Grid>
                    <ArgentinaProvinceCityFields
                      stateValue={addressFormData.state}
                      cityValue={addressFormData.city}
                      onStateCityChange={(state, city) =>
                        setAddressFormData({ ...addressFormData, state, city })
                      }
                    />
                    <Grid xs={12} md={6}>
                      <FormControl required>
                        <FormLabel>Código postal</FormLabel>
                        <Input
                          value={addressFormData.zipCode}
                          onChange={(e) =>
                            setAddressFormData({ ...addressFormData, zipCode: e.target.value })
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Descripción</FormLabel>
                        <Input
                          value={addressFormData.referenceDescription}
                          onChange={(e) =>
                            setAddressFormData({ ...addressFormData, referenceDescription: e.target.value })
                          }
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Checkbox
                    sx={{ mt: 1 }}
                    label="Dirección principal"
                    checked={addressFormData.isDefault}
                    onChange={(e) =>
                      setAddressFormData({ ...addressFormData, isDefault: e.target.checked })
                    }
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button type="button" variant="outlined" color="neutral" onClick={() => setShowAddressForm(false)}>
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      color="primary"
                      onClick={() => void commitInlineAddress()}
                    >
                      Guardar dirección
                    </Button>
                  </Stack>
                </Box>
              </Sheet>
            ) : null}

            <Grid container spacing={LAYOUT.gridSpacing}>
              {addresses.map((address, index) => (
                <Grid key={index} xs={12} md={6}>
                  <Sheet variant="outlined" color={address.isDefault ? 'success' : 'neutral'} sx={{ p: LAYOUT.cardPaddingDense, borderRadius: 'md' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        {address.isDefault ? (
                          <Chip size="sm" color="success" variant="soft" sx={{ mb: 1 }}>
                            Principal
                          </Chip>
                        ) : null}
                        <Typography level="title-sm">
                          {address.street} {address.number}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                          {address.city}, {address.state}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                          CP: {address.zipCode}
                        </Typography>
                        {address.reference ? (
                          <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1, fontStyle: 'italic' }}>
                            Ref: {formatRecordReference(address.reference)}
                          </Typography>
                        ) : null}
                      </Box>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleRemoveAddress(address)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  </Sheet>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box
            sx={{
              px: LAYOUT.cardPaddingLg,
              py: LAYOUT.stackTight,
              bgcolor: 'background.level1',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" justifyContent="flex-end">
              <Button type="submit" color="primary" loading={loading}>
                {clientReference ? 'Actualizar' : 'Crear'} cliente
              </Button>
            </Stack>
          </Box>
        </Box>
      </Sheet>
    </Stack>
  );
};

export default ClientForm;
